import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

export const Terms: React.FC = () => {
  const { t } = useTranslation();
  
  return (
    <>
      <Helmet>
        <title>{t('metadata.terms.title')}</title>
        <meta name="description" content={t('metadata.terms.description')} />
        <link rel="canonical" href="https://iptvplayer.stream/terms" />
      </Helmet>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-8">Terms of Service</h1>
        
        <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-6 space-y-8">
          <section>
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">1. Acceptance of Terms</h2>
            <p className="text-gray-600 dark:text-gray-400">
              By accessing and using IPTVPlayer.stream, you accept and agree to be bound by the terms and
              provisions of this agreement. These terms should be read together with our{' '}
              <Link 
                to="/privacy" 
                className="text-blue-500 hover:text-blue-600 dark:text-blue-400 dark:hover:text-blue-300 transition-colors"
              >
                Privacy Policy
              </Link>. If you do not agree to these terms, please do not use our service.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">2. Service Description</h2>
            <p className="text-gray-600 dark:text-gray-400">
              IPTV Player is a web-based application that allows users to stream content from their own M3U8/HLS sources.
              We do not provide any content directly and are not responsible for the content that users choose to stream.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">3. User Responsibilities</h2>
            <div className="space-y-4">
              <p className="text-gray-600 dark:text-gray-400">Users agree to:</p>
              <ul className="list-disc pl-6 text-gray-600 dark:text-gray-400 space-y-2">
                <li>Provide accurate account information</li>
                <li>Maintain the security of their account</li>
                <li>Not use the service for any illegal purposes</li>
                <li>Not attempt to circumvent any security features</li>
                <li>Not distribute or share copyrighted material without permission</li>
              </ul>
            </div>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">4. Content Policy</h2>
            <p className="text-gray-600 dark:text-gray-400">
              Users are solely responsible for the content they stream through our service. We do not monitor,
              approve, or endorse any content streamed through the platform. Users must ensure they have the
              right to access and stream any content they use with our service.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">5. Service Modifications</h2>
            <p className="text-gray-600 dark:text-gray-400">
              We reserve the right to modify or discontinue the service at any time, with or without notice.
              We shall not be liable to you or any third party for any modification, suspension, or
              discontinuance of the service.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">6. Contact</h2>
            <p className="text-gray-600 dark:text-gray-400">
              If you have any questions about these Terms, please{' '}
              <Link 
                to="/contact" 
                className="text-blue-500 hover:text-blue-600 dark:text-blue-400 dark:hover:text-blue-300 transition-colors"
              >
                contact us
              </Link>
              {' '}or email us at{' '}
              <a 
                href="mailto:support@iptvplayer.stream"
                className="text-blue-500 hover:text-blue-600 dark:text-blue-400 dark:hover:text-blue-300 transition-colors"
              >
                support@iptvplayer.stream
              </a>
              . For general help and information, visit our{' '}
              <Link 
                to="/m3u-hls-player-faq" 
                className="text-blue-500 hover:text-blue-600 dark:text-blue-400 dark:hover:text-blue-300 transition-colors"
              >
                FAQ section
              </Link>.
            </p>
          </section>

          <div className="text-sm text-gray-500 dark:text-gray-400 pt-8 border-t border-gray-200 dark:border-gray-700">
            Last updated: {new Date().toLocaleDateString()}
          </div>
        </div>
      </div>
    </>
  );
}; 